import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ClipboardPermissionService } from '@app/services/clipboard-permission-service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrFactoryService } from 'src/app/services/toastr-factory.service';

@Component({
  selector: 'app-shared-input',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, TranslateModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: SharedInputComponent,
      multi: true,
    },
  ],
  templateUrl: './shared-input.component.html',
})
export class SharedInputComponent implements ControlValueAccessor, OnInit {
  protected toastrFactory = inject(ToastrFactoryService);
  protected translate = inject(TranslateService);
  protected clipboardPermissionService = inject(ClipboardPermissionService);

  public clipboardDenied = false;

  @Input() label: string = '';
  @Input() type: string = 'text';
  @Input() placeholder: string = '';
  @Input() inputId: string | undefined;
  @Input() maxLength: number | null = null;
  @Input() disabled: boolean = false;
  @Input() control: any;
  @Input() errorMessage: string = 'invalidInput';
  @Input() showError: boolean = false;
  @Input() inputClass: string = 'w-full px-4 py-2.5 border border-gray-500 rounded-lg  bg-white';
  @Input() icon?: string;
  @Input() iconPosition: 'left' | 'right' = 'left';
  @Input() noMarginTop = false;
  @Input() fullWidthInput = false;

  @Input() copyFromClipboard = false;
  @Input() copyFromClipboardLink = '';

  public onChange: (value: any) => void = () => {};
  public onTouched: () => void = () => {};

  writeValue(value: any): void {
    if (this.control) {
      this.control.setValue(value);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  onInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const value = inputElement.value;
    if (inputElement) {
      this.onChange(this.maxLength ? value?.slice(0, this.maxLength) : value);
    }
  }

  async copyFromClipboardFunction(): Promise<void> {
    try {
      const clipboardContent = await navigator.clipboard.readText();
      this.control.setValue(clipboardContent.trim());
    } catch (e: any) {
      console.log(e);
      this.toastrFactory.error(this.translate.instant('permissionToReadFromYourClipboardHasBeenDenied'));
      return;
    }
  }

  get isRequired(): boolean {
    if (this.control?.hasValidator(Validators.required)) {
      return true;
    }
    return false;
  }

  get inputContainerClass(): string {
    return `relative ${this.icon ? 'flex items-center' : ''}`;
  }

  get modifiedInputClass(): string {
    if (!this.icon) return this.inputClass;

    return `${this.inputClass} ${this.iconPosition === 'left' ? 'pl-10' : 'pr-10'}`;
  }

  async ngOnInit(): Promise<void> {
    this.clipboardDenied = !(await this.clipboardPermissionService.checkPermission());
  }
}
